import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { suggestedMarkerLabel } from './gaBalloonUtils';

function activityMatchesQuery(activity, q) {
  if (!q) return true;
  return (
    String(activity.task_code || '').toLowerCase().includes(q) ||
    String(activity.task_name || '').toLowerCase().includes(q) ||
    String(activity.wbs_path || '').toLowerCase().includes(q)
  );
}

/**
 * ActivityPicker - modal to bind a marker to schedule data.
 * Two modes:
 *  - "wbs": pick a WBS branch; the marker follows every activity under it
 *  - "activities": hand-pick individual activities by ID
 * Bindings are stored by task_code / WBS path, so they keep resolving when a
 * new weekly snapshot is imported.
 *
 * Already-chosen activities stay in a strip above the list (remove with ×)
 * and can be shown on their own, so unchecking does not mean scrolling the
 * whole schedule. A chosen WBS path stays pinned the same way.
 */
const ActivityPicker = ({ jobNumber, snapshotId, rev, initialBinding, onSave, onClose }) => {
  const { instance, accounts } = useMsal();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState(initialBinding?.mode || 'wbs');
  const [selectedCodes, setSelectedCodes] = useState(new Set(initialBinding?.taskCodes || []));
  const [selectedWbs, setSelectedWbs] = useState(initialBinding?.wbsPath || '');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const selectedWbsRef = useRef(null);
  const scrolledWbsRef = useRef(false);
  const pendingWbsScrollRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (snapshotId) {
          params.set('snapshotId', snapshotId);
          params.set('source', 'snapshot');
        } else if (rev) {
          params.set('rev', rev);
        }
        const qs = params.toString();
        const res = await authenticatedApiCall(
          instance, accounts, 'GET',
          `/ga-dashboards/${jobNumber}/activities${qs ? `?${qs}` : ''}`
        );
        setActivities(res.data?.data?.activities || []);
      } catch (e) {
        console.error('Failed to load activities:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [instance, accounts, jobNumber, snapshotId, rev]);

  const wbsBranches = useMemo(() => {
    const set = new Map(); // path -> count
    for (const a of activities) {
      const parts = (a.wbs_path || '').split(' > ').filter(Boolean);
      let acc = '';
      for (const part of parts) {
        acc = acc ? `${acc} > ${part}` : part;
        set.set(acc, (set.get(acc) || 0) + 1);
      }
    }
    return [...set.entries()]
      .map(([path, count]) => ({ path, count, depth: path.split(' > ').length }))
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [activities]);

  const q = search.trim().toLowerCase();
  const activityByCode = useMemo(() => {
    const map = new Map();
    for (const a of activities) map.set(a.task_code, a);
    return map;
  }, [activities]);
  const filteredActivities = useMemo(
    () => activities.filter((a) => activityMatchesQuery(a, q)),
    [activities, q]
  );
  const selectedActivityRows = useMemo(() => {
    const rows = [];
    for (const code of selectedCodes) {
      const known = activityByCode.get(code);
      if (known) {
        if (activityMatchesQuery(known, q)) rows.push(known);
      } else if (!q || code.toLowerCase().includes(q)) {
        rows.push({
          task_code: code,
          task_name: 'Not in this snapshot',
          wbs_path: '',
          missing: true,
        });
      }
    }
    return rows;
  }, [selectedCodes, activityByCode, q]);
  const visibleActivities = selectedOnly ? selectedActivityRows : filteredActivities;
  const filteredBranches = useMemo(
    () => wbsBranches.filter((b) => !q || b.path.toLowerCase().includes(q)),
    [wbsBranches, q]
  );

  useEffect(() => {
    if (selectedOnly && selectedCodes.size === 0) setSelectedOnly(false);
  }, [selectedOnly, selectedCodes]);

  useEffect(() => {
    if (loading || mode !== 'wbs' || !selectedWbs) return undefined;
    if (scrolledWbsRef.current && !pendingWbsScrollRef.current) return undefined;
    const id = requestAnimationFrame(() => {
      if (!selectedWbsRef.current) return;
      selectedWbsRef.current.scrollIntoView({ block: 'center' });
      scrolledWbsRef.current = true;
      pendingWbsScrollRef.current = false;
    });
    return () => cancelAnimationFrame(id);
  }, [loading, mode, selectedWbs, filteredBranches.length, q]);

  const revealSelectedWbs = () => {
    if (q && !selectedWbs.toLowerCase().includes(q)) {
      pendingWbsScrollRef.current = true;
      setSearch('');
      return;
    }
    selectedWbsRef.current?.scrollIntoView({ block: 'center' });
  };

  const toggleCode = (code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const handleSave = () => {
    const binding = mode === 'wbs'
      ? { mode: 'wbs', wbsPath: selectedWbs }
      : { mode: 'activities', taskCodes: [...selectedCodes] };
    if (mode === 'wbs' && !selectedWbs) return;
    if (mode !== 'wbs' && selectedCodes.size === 0) return;
    onSave(binding, suggestedMarkerLabel({ binding, activities }));
  };

  const btn = (active) => ({
    padding: '6px 14px', borderRadius: 8, border: '1px solid #cbd5e1', cursor: 'pointer',
    background: active ? '#0f766e' : '#fff', color: active ? '#fff' : '#334155', fontWeight: 600, fontSize: 13,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 720, maxWidth: '94vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>Bind marker to schedule</strong>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '12px 18px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          <button type="button" style={btn(mode === 'wbs')} onClick={() => { setMode('wbs'); setSelectedOnly(false); }}>WBS area (auto-follows)</button>
          <button type="button" style={btn(mode === 'activities')} onClick={() => { setMode('activities'); setSelectedOnly(false); }}>Pick activities</button>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, name or WBS…"
            style={{ flex: 1, minWidth: 180, padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>

        {!loading && mode === 'activities' && selectedCodes.size > 0 && (
          <div style={{ padding: '8px 18px 10px', borderBottom: '1px solid #e2e8f0', background: '#f0fdfa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: selectedOnly ? 0 : 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0f766e' }}>
                {selectedCodes.size} selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedOnly((v) => !v)}
                style={{ background: 'none', border: 'none', color: '#0f766e', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                {selectedOnly ? 'Show all activities' : (selectedCodes.size === 1 ? 'Show only this' : 'Show only these')}
              </button>
            </div>
            {!selectedOnly && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 108, overflowY: 'auto' }}>
                {[...selectedCodes].map((code) => {
                  const known = activityByCode.get(code);
                  const missing = !known;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleCode(code)}
                      title={missing ? `${code} is not in this snapshot. Click to remove.` : `Remove ${code}${known.task_name ? ` — ${known.task_name}` : ''}`}
                      aria-label={`Remove ${code}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
                        border: `1px solid ${missing ? '#d97706' : '#0f766e'}`,
                        background: missing ? '#fef3c7' : '#ccfbf1',
                        color: missing ? '#92400e' : '#134e4a',
                        fontSize: 12, fontWeight: 600,
                      }}
                    >
                      {code}
                      <span aria-hidden="true" style={{ fontWeight: 700, lineHeight: 1 }}>×</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!loading && mode === 'wbs' && selectedWbs && (
          <div style={{ padding: '8px 18px', borderBottom: '1px solid #e2e8f0', background: '#f0fdfa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ minWidth: 0, fontSize: 13 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0f766e' }}>Selected WBS</div>
              <div style={{ color: '#134e4a', wordBreak: 'break-word' }}>{selectedWbs}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={revealSelectedWbs}
                style={{ background: 'none', border: 'none', color: '#0f766e', fontWeight: 700, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Show in list
              </button>
              <button
                type="button"
                onClick={() => setSelectedWbs('')}
                style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #0f766e', background: '#fff', color: '#0f766e', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 18px' }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Loading schedule activities…</div>
          ) : mode === 'wbs' ? (
            filteredBranches.map((b) => (
              <div
                key={b.path}
                ref={selectedWbs === b.path ? selectedWbsRef : null}
                onClick={() => setSelectedWbs(b.path)}
                style={{
                  padding: '6px 10px', paddingLeft: 10 + (b.depth - 1) * 18, borderRadius: 6, cursor: 'pointer',
                  background: selectedWbs === b.path ? '#ccfbf1' : 'transparent',
                  border: selectedWbs === b.path ? '1px solid #0f766e' : '1px solid transparent',
                  display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2,
                }}
              >
                <span style={{ fontWeight: b.depth <= 2 ? 700 : 400 }}>{b.path.split(' > ').pop()}</span>
                <span style={{ color: '#64748b' }}>{b.count} act.</span>
              </div>
            ))
          ) : (
            <>
            {selectedOnly && q && visibleActivities.length > 0 && visibleActivities.length < selectedCodes.size && (
              <div style={{ fontSize: 12, color: '#64748b', padding: '4px 8px 8px' }}>
                {visibleActivities.length} of {selectedCodes.size} selected match this search.
              </div>
            )}
            {visibleActivities.map((a) => (
              <label
                key={a.task_code}
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 8px', borderRadius: 6,
                  cursor: 'pointer',
                  background: a.missing ? '#fef3c7' : (selectedCodes.has(a.task_code) ? '#ccfbf1' : 'transparent'),
                  fontSize: 13,
                }}
              >
                <input type="checkbox" checked={selectedCodes.has(a.task_code)} onChange={() => toggleCode(a.task_code)} style={{ marginTop: 3 }} />
                <span>
                  <strong>{a.task_code}</strong> — {a.task_name}
                  <span style={{ color: '#64748b', display: 'block', fontSize: 11 }}>
                    {a.missing
                      ? 'This code is saved on the marker but is not in the schedule you are viewing.'
                      : `${a.wbs_path} · ${(a.act_start || a.target_start || '—')} → ${(a.act_end || a.target_end || '—')}`}
                  </span>
                </span>
              </label>
            ))}
            </>
          )}
          {!loading && mode === 'activities' && selectedOnly && visibleActivities.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>
              No selected activities match that search.
            </div>
          )}
          {!loading && !activities.length && (
            <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>
              No schedule snapshot imported yet. Import an XER/XLSX first.
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {mode === 'wbs'
              ? (selectedWbs ? `Following: ${selectedWbs}` : 'Pick a WBS area')
              : `${selectedCodes.size} activit${selectedCodes.size === 1 ? 'y' : 'ies'} selected`}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>Cancel</button>
            <button
              onClick={handleSave}
              disabled={mode === 'wbs' ? !selectedWbs : selectedCodes.size === 0}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: (mode === 'wbs' ? !selectedWbs : selectedCodes.size === 0) ? 0.5 : 1 }}
            >
              Save binding
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityPicker;
